/** Route slug ↔ `/legal/estate-planning-inheritance`. */
export const ESTATE_PLANNING_INHERITANCE_SLUG = "estate-planning-inheritance";

export const estatePlanningInheritanceDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "Act 360", label: "Wills" },
    { value: "PNDCL 111", label: "Intestacy" },
    { value: "HC", label: "Probate" },
  ],
  funnelTagline:
    "Wills → probate or letters → lawful distribution → plan early",
  focusAreas: ["Wills & executors", "Probate", "Letters of administration", "Succession disputes"],
  primaryCtaHeading: "Speak to an estate lawyer",
  introduction: {
    headline: "WHY ESTATE PLANNING & INHERITANCE LAW MATTERS IN GHANA",
    lead:
      "Estate planning is about making sure your property, money, land, business interests, and family duties are handled properly if you die or become unable to manage them.",
    mixtureLead:
      "In Ghana this area mixes statutory rules, court process, and customary practice—so confusion after death is common.",
    frameworkBullets: [
      "Wills Act, 1971 (Act 360) — wills",
      "Intestate Succession Law, 1985 (PNDCL 111) — dying without a will",
      "Probate and administration handled through the courts",
    ],
    risksHeading: "For most families, biggest risks:",
    risks: [
      "Dying without a will",
      "Family fights over land or houses",
      "Delays getting access to bank accounts or property",
      "Disputes between spouse, children, parents, and extended family",
      "Not understanding when probate or letters of administration are needed",
    ],
  },
  testateVsIntestate: {
    title: "THE CORE DIFFERENCE: TESTATE VS INTESTATE",
    testateHeading: "If a person dies testate",
    testateBody:
      "They left a valid will. The estate is generally administered according to that will, subject to law and court process. The Wills Act regulates who can make a will and formalities.",
    intestateHeading: "If a person dies intestate",
    intestateBody:
      "They died without a will. Distribution is controlled mainly by the Intestate Succession Law, 1985 (PNDCL 111), not personal wishes standing alone.",
    whyHeading: "Why this matters",
    whyBody:
      "Testacy gives clearer instructions and executors. Intestacy leaves the family to statute, court process, and negotiation—often fueling fights over houses, land, cash, and who may act.",
  },
  willsSection: {
    title: "WILLS IN GHANA",
    definitionLead:
      "A will is a legal document stating how estate should be handled after death. Under Act 360, a person aged 18+ and of sound mind generally may make a will, with safeguards for dependents.",
    whyHeading: "Why making a will matters",
    whyBullets: [
      "Choose who receives what",
      "Appoint executors",
      "Reduce family confusion",
      "Protect vulnerable dependants",
      "Make administration smoother",
    ],
    coverHeading: "What a good will usually covers",
    coverBullets: [
      "Houses and land",
      "Bank accounts",
      "Businesses or shares",
      "Cars and personal items",
      "Guardianship wishes for children",
      "Debts and obligations",
      "Appointment of executors",
    ],
    mistakesHeading: "Common mistakes",
    mistakes: [
      "Not making one at all",
      "Vague language",
      "Not updating after marriage, divorce, births, or major purchases",
      "Hiding the will so no one can find it",
      "Informal documents that may be challenged",
    ],
  },
  willValidity: {
    title: "WHO CAN MAKE A WILL AND WHAT MAKES IT VALID",
    checklistLead: "A testator should:",
    checklist: ["Be legally qualified", "Understand what they own", "Understand who may benefit", "Sign with Wills Act formalities"],
    formsNote:
      "Judicial Service forms connected to proof of due execution show that execution is a real court issue in probate.",
    practicalMessage:
      "It is not just writing wishes—they must be prepared and executed so they stand up when probate is sought.",
  },
  probateSection: {
    title: "PROBATE WHEN THERE IS A WILL",
    intro:
      "Probate confirms the executor’s authority to administer the estate. Probate and administration fall under High Court probate jurisdiction—the Judicial Service’s Probate and Administration Division handles these matters.",
    executorPowersHeading: "What probate allows the executor to do",
    executorPowers: [
      "Deal with the deceased’s property",
      "Access or transfer assets",
      "Pay debts where necessary",
      "Distribute the estate according to the will",
    ],
    problemHeading: "Common practical gap",
    problemBody:
      "Many assume a will alone is enough—but banks and other institutions usually require court-backed authority via probate before releasing or transferring assets.",
  },
  lettersSection: {
    title: "LETTERS OF ADMINISTRATION (NO WILL)",
    intro:
      "Without a valid will or effective executor, families usually need Letters of Administration—a court authorization for an administrator. Official forms exist for Letters (not will annexed), next-of-kin notices, oath, and property declarations.",
    adminDutiesHeading: "What the administrator does",
    adminDuties: [
      "Gathers the estate",
      "Pays lawful debts and expenses",
      "Distributes according to applicable law—usually PNDCL 111 for intestacy",
    ],
    withoutHeading: "Why Letters matter",
    withoutBody:
      "Without Letters, families struggle to deal with land records, bank funds, rental income, and other assets—even when heirs agree morally.",
  },
  intestacyDetail: {
    title: "INTESTATE SUCCESSION (PNDCL 111)",
    bodyLead:
      "Where there is no will, PNDCL 111 sets how parts of the estate are distributed among surviving spouse, children, parents, and customary family interests, depending on who survives.",
    householdRuleLead: "Widely cited protections",
    householdRuleBody:
      "Household chattels of the intestate devolve to surviving spouse and children; where only one house is left, it generally goes to spouse and children in the manner provided by law.",
    confusionHeading: "Unsafe assumptions people make",
    confusionBullets: [
      "Eldest child controls everything",
      "Extended family always takes all",
      "Spouse takes all alone",
      "Custom always overrides statute",
    ],
    interactionNote:
      "Statutory rules matter greatly in intestacy; customary succession can still intersect with specific assets—professional advice is essential.",
  },
  distributionGuide: {
    title: "ESTATE DISTRIBUTION: WHAT FAMILIES SHOULD UNDERSTAND",
    assetsHeading: "Assets that may form part of the estate",
    assets: [
      "Land and buildings",
      "Money in bank accounts",
      "Vehicles",
      "Shares and business interests",
      "Rent receivables",
      "Household items",
      "Other personal property",
    ],
    driversHeading: "Things that affect distribution",
    drivers: [
      "Valid will or intestacy",
      "Whether deceased was married",
      "Children surviving",
      "Parents surviving",
      "Self-acquired vs family/customary property debates",
      "Debts or competing claims",
    ],
    stepsHeading: "Before informal distribution—determine:",
    steps: ["Is there a will?", "Who has legal authority?", "What assets belonged to the deceased?", "What does applicable law require?"],
    caution:
      "Distribution should not start casually because funeral rites are done—establish authority and law first.",
  },
  disputesSection: {
    title: "SUCCESSION DISPUTES",
    causesHeading: "Common causes",
    causes: [
      "No will",
      "Multiple persons claiming authority",
      "Land ownership disputes",
      "Hidden assets",
      "Self-acquired vs family property confusion",
      "Conflict between spouse/children and extended family",
      "Disputes over customary expectations",
    ],
    questionsHeading: "Typical questions in dispute",
    questions: [
      "Who is the rightful administrator?",
      "Can family head distribute before Letters?",
      "Is the matrimonial home protected?",
      "Custom vs statute for particular property?",
      "Family property vs personal property?",
    ],
    actionHeading: "If there is dispute",
    actions: ["Do not rush to sell or transfer", "Gather documents", "Identify whether probate or Letters issued", "Avoid private distribution without authority", "Get legal help quickly"],
  },
  courtRole: {
    title: "HIGH COURT & PROBATE DIVISION",
    body:
      "The High Court has jurisdiction over probate and letters of administration. Inheritance involves court-regulated process—proving wills, appointing administrators, entitlement disputes, property disputes, and restraining dissipation.",
    triggersHeading: "Especially where there is:",
    triggers: ["A will to prove", "Administrators needed", "Entitlement disputes", "Disputes over estate property"],
  },
  commonProblemsGhana: {
    title: "COMMON PROBLEMS GHANAIANS FACE IN ESTATE MATTERS",
    items: [
      "Parents die without wills",
      "Children cannot find title documents",
      "Family take property before court authority",
      "Widows or widowers pressured or excluded",
      "Bank accounts and rents inaccessible",
      "Unclear land records",
      "Custom clashes with statute",
      "Relatives claim powers they lack legally",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS",
    planning: {
      heading: "If you are planning ahead",
      items: ["Make a clear will", "Update after family or assets change", "Keep a reliable asset list", "Choose trustworthy executors", "Tell a trusted person where documents are"],
    },
    afterDeath: {
      heading: "If a relative has just died",
      items: ["Confirm whether there is a will", "Secure documents and property", "Do not rush distribution", "Check probate or Letters needs", "Record assets and debts"],
    },
    disputeTip: {
      heading: "If there is a dispute",
      items: ["Avoid self-help or force", "Do not sell estate property without authority", "Get legal advice early", "Use court process where necessary"],
    },
  },
  redFlags: {
    title: "RED FLAGS TO WATCH OUT FOR",
    items: [
      "No will and no clear administrator",
      "Immediate distribution after death",
      "Sale of land or house before probate/Letters",
      "Hidden title documents",
      "Forged or suspicious late wills",
      "Disputes over personal vs family property",
      "Pressure on surviving spouse to leave home quickly",
    ],
  },
  editorialPlaybook: {
    title: "HIGH-VALUE CONTENT TOPICS",
    willsPages: ["How to Make a Valid Will in Ghana", "What Makes a Will Invalid", "Why Property Owners Should Have a Will"],
    probatePages: ["Probate in Ghana: What Executors Must Do", "How Long Probate Can Take", "Documents Families Usually Need"],
    lettersPages: ["When Someone Dies Without a Will", "Who Can Apply for Letters of Administration", "Mistakes in Intestate Estates"],
    distributionPages: ["Who Inherits Under PNDCL 111", "Widow/Widower and the House", "Self-Acquired vs Family Property"],
    disputePages: ["Handling Succession Disputes", "Fighting Relatives Over Property", "When to Go to Court"],
    videos: ["Executor basics", "Letters vs probate", "Spotting wrongful transfers"],
    leadCtasHeading: "Suggested CTAs",
    leadCtas: [
      "Speak to an estate lawyer",
      "Help me prepare a will",
      "Start probate process",
      "Apply for letters of administration",
      "Resolve an inheritance dispute",
      "Protect my family home",
      "Book estate planning consultation",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hooksHeading: "Hook",
    hooks: [
      "What happens if my father dies without a will?",
      "How do I protect my children and spouse?",
      "Can family take property before probate?",
    ],
    education: {
      label: "Education",
      text: "Wills, probate, letters, intestacy, spouse/child protections, dispute prevention.",
    },
    authority: { label: "Authority", text: "Court structure, Judicial Service forms, succession laws." },
    conversion: {
      label: "Conversion",
      text: "Will support, probate, letters, dispute review, consultations.",
    },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    pillars: [
      "Protect spouse and children",
      "Reduce conflict",
      "Preserve family property",
      "Lawful administration and receipts",
    ],
    messageLead: "Biggest problems stem from:",
    messageMistakes: ["Delay", "Informality", "Wrong assumptions"],
    safeMessage:
      "If property matters to your family, planning early is cheaper and safer than fighting later.",
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "Estate planning is about life after you—handled lawfully—not chaos.",
    smooth: "Wills executed · grants obtained · orderly sharing",
    and: "vs",
    stressful: "Intestacy feuds · frozen accounts · lost titles",
    closing: "The safest move is the informed one—made early.",
  },
  faqs: [
    {
      q: "What happens if my parent died without a will?",
      a: "The estate should not be shared casually. Letters of Administration are usually needed, and distribution follows intestacy rules, especially PNDCL 111.",
    },
    {
      q: "What is the difference between probate and letters of administration?",
      a: "Probate proves a will and empowers executors. Letters of Administration apply where there is no will or no effective executor.",
    },
    {
      q: "Can a spouse be pushed out of the house after the other dies?",
      a: "Intestate succession law gives important protection to spouses and children, including household chattels and provisions around a sole house.",
    },
    {
      q: "Can family share property before court papers?",
      a: "That is risky; proper probate or Letters are usually needed for lawful administration.",
    },
    {
      q: "Do I need a lawyer if there is a will?",
      a: "Often yes—probate, due execution proof, and administration still require formal steps and guided filings.",
    },
  ],
  articles: [
    {
      id: "epi-a-1",
      slug: "estate-valid-will-ghana-act-360",
      title: "How to Make a Valid Will in Ghana",
      excerpt:
        "Capacity under Act 360, witnesses, dependants, updating wills, safeguarding originals—what survives probate scrutiny.",
      type: "Guide",
      readTime: "11 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: "How to Make a Valid Will in Ghana",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      tags: ["Act 360", "Executors", "Ghana"],
      pullQuote: "Execution formalities—not wishes alone—decide probate success.",
      takeaways: ["Name alternates", "Match assets to clauses", "Store originals safely"],
      video: {
        title: "Executor basics",
        duration: "6:05",
        poster:
          "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        { heading: "Dependent relief", text: "Courts may temper dispositions affecting maintenance obligations." },
        { heading: "Codicils", text: "Amend cleanly; inconsistent scraps invite litigation." },
      ],
    },
    {
      id: "epi-a-2",
      slug: "estate-probate-letters-administration-ghana",
      title: "Probate vs Letters of Administration",
      excerpt:
        "High Court filings, Judicial Service forms, creditor notices, fiduciary duties—the difference grants make for banks and Lands Commission.",
      type: "Checklist",
      readTime: "9 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: "Probate vs Letters of Administration in Ghana",
      coverImage:
        "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=800&q=80",
      tags: ["High Court", "Grants", "Forms"],
      pullQuote: "Institutions release assets against grants—not verbal consensus.",
      takeaways: ["Track filing timelines", "Keep heir affidavits ready"],
      video: {
        title: "Letters vs probate",
        duration: "5:40",
        poster:
          "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "Caveats", text: "Objectors may stall—plan mediation or contested directions." }],
    },
    {
      id: "epi-a-3",
      slug: "estate-pndcl-111-intestate-succession-ghana",
      title: "Who Inherits Under PNDCL 111?",
      excerpt:
        "Statutory shares, matrimonial protections, customary overlap, and why early Letters prevent grab-and-dash.",
      type: "Expert Explainer",
      readTime: "10 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: "Ghana intestate succession basics — PNDCL 111",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      tags: ["Intestacy", "Spouse", "Children"],
      pullQuote: "Percentages—not sentiment—guide lawful division.",
      takeaways: ["Model survivor sets", "Document clan land sensitivities"],
      video: {
        title: "Spotting wrongful transfers",
        duration: "4:55",
        poster:
          "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "Post-grant accountability", text: "Administrators owe estate accounts—ask when distribution stalls." }],
    },
  ],
  videos: [
    {
      id: "epi-v-1",
      title: "Executor basics",
      duration: "6:05",
      poster:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      description: "First steps after death when a will names you.",
    },
    {
      id: "epi-v-2",
      title: "Letters vs probate",
      duration: "5:40",
      poster:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80",
      description: "Which grant applies and why banks care.",
    },
    {
      id: "epi-v-3",
      title: "Spotting wrongful transfers",
      duration: "4:55",
      poster:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      description: "Red flags before property walks away.",
    },
  ],
  podcasts: [
    {
      id: "epi-p-1",
      episodeLabel: "Episode 1",
      duration: "36 min",
      title: "Act 360 Essentials for Everyday Families",
      description: "Clauses wills should not skip.",
      host: "Afrimore Legal Desk",
      publishedAt: "Jun 2026",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "epi-p-2",
      episodeLabel: "Episode 2",
      duration: "33 min",
      title: "Behind Probate Registry Counters",
      description: "What delays filings—and how to unstick.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "epi-p-3",
      episodeLabel: "Mini",
      duration: "19 min",
      title: "Letters Docket FAQs",
      description: "Heir disputes without a will.",
      host: "Afrimore Legal Desk",
      publishedAt: "Apr 2026",
      coverImage:
        "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
